import { ExpandLess, ExpandMore, Mail as MailIcon } from '@mui/icons-material';
import { InputLabel, Select, TextField } from '@mui/material';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { postActionCenterAdvocacyAnalyticsReport } from 'store/actions/advocacyReports';
import { setSnackbarError, setSnackbarSuccess } from 'store/actions/global';
import {
  BuildButton,
  DatesWrapper,
  OptionsSection,
  OptionsToggle,
  ReportDescription,
  ReportTitle,
  StyledCard,
  StyledFormControl,
} from '../styles';

const ActionCenterAnalyticsReport = ({ actionCenterUuid, organizationUuid, reportsSchema }) => {
  const dispatch = useDispatch();

  const [selectedActionCenter, setSelectedActionCenter] = useState('');
  const [showOptions, setShowOptions] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const handleOptionsToggle = () => setShowOptions(prevState => !prevState);

  const handleBuildReport = () => {
    if (actionCenterUuid) {
      const payload = {
        action_center: actionCenterUuid,
      };

      if (startDate) {
        payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
      }

      if (endDate) {
        payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
      }

      dispatch(postActionCenterAdvocacyAnalyticsReport(payload, organizationUuid))
        .then(result => {
          dispatch(setSnackbarSuccess({ message: result?.detail }));
        })
        .catch(() => {
          dispatch(
            setSnackbarError({
              message: 'Failed to build report. Make sure all fields are set properly',
            })
          );
        });

      return;
    }

    if (!selectedActionCenter) {
      dispatch(setSnackbarError({ message: 'Please select an action' }));

      return;
    }

    const foundActionCenter = reportsSchema?.campaigns
      ?.flatMap(campaign => campaign.actionCenters || [])
      .find(ac => ac.uuid === selectedActionCenter);

    if (!foundActionCenter) return;

    const payload = {
      action_center: foundActionCenter.uuid,
    };

    if (startDate) {
      payload.start_date = dayjs(startDate).format('YYYY-MM-DD');
    }

    if (endDate) {
      payload.end_date = dayjs(endDate).format('YYYY-MM-DD');
    }

    dispatch(postActionCenterAdvocacyAnalyticsReport(payload, organizationUuid))
      .then(result => {
        dispatch(setSnackbarSuccess({ message: result?.detail }));
      })
      .catch(() => {
        dispatch(
          setSnackbarError({
            message: 'Failed to build report. Make sure all fields are set properly',
          })
        );
      });
  };

  return (
    <StyledCard variant="outlined">
      <ReportTitle variant="h6">Action Center Analytics Report</ReportTitle>
      <ReportDescription variant="body2">
        {actionCenterUuid
          ? 'Export analytics for this action center.'
          : 'Export analytics for a specific action center.'}
      </ReportDescription>

      {!actionCenterUuid && (
        <StyledFormControl variant="outlined">
          <InputLabel
            htmlFor="action-center-native-select-analytics"
            sx={{ backgroundColor: 'white' }}
          >
            Action Center
          </InputLabel>
          <Select
            variant="outlined"
            native
            value={selectedActionCenter}
            onChange={e => setSelectedActionCenter(e.target.value)}
            label="Action Center"
            inputProps={{
              id: 'action-center-native-select-analytics',
              name: 'actionCenter',
            }}
          >
            <option aria-label="None" value="" />
            {reportsSchema?.campaigns?.map(campaign => (
              <optgroup key={campaign.uuid} label={campaign.name}>
                {campaign.actionCenters?.map(ac => (
                  <option key={ac.uuid} value={ac.uuid} disabled={!ac.showMessages}>
                    {ac.name}
                  </option>
                ))}
              </optgroup>
            ))}
          </Select>
        </StyledFormControl>
      )}

      <OptionsToggle onClick={handleOptionsToggle}>
        {showOptions ? 'Hide options' : 'Options'}
        {showOptions ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
      </OptionsToggle>

      {showOptions && (
        <OptionsSection>
          <DatesWrapper>
            <TextField
              label="Start date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
            <TextField
              label="End date"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              slotProps={{
                inputLabel: { shrink: true },
              }}
            />
          </DatesWrapper>
        </OptionsSection>
      )}

      <BuildButton
        variant="contained"
        color="primary"
        startIcon={<MailIcon />}
        onClick={handleBuildReport}
      >
        Build Report
      </BuildButton>
    </StyledCard>
  );
};

export default ActionCenterAnalyticsReport;

ActionCenterAnalyticsReport.defaultProps = {
  actionCenterUuid: null,
  reportsSchema: null,
};

ActionCenterAnalyticsReport.propTypes = {
  actionCenterUuid: PropTypes.string,
  organizationUuid: PropTypes.string.isRequired,
  reportsSchema: PropTypes.shape({
    campaigns: PropTypes.arrayOf(
      PropTypes.shape({
        actionCenters: PropTypes.arrayOf(
          PropTypes.shape({
            name: PropTypes.string,
            showMessages: PropTypes.bool,
            uuid: PropTypes.string,
          })
        ),
        name: PropTypes.string,
        uuid: PropTypes.string,
      })
    ),
    showAllAdvocacy: PropTypes.bool,
  }),
};
